import * as React from 'react';
import { withApollo } from 'react-apollo';

import type { ApolloClient } from '@apollo/client';
import _ from 'lodash';
import { compose } from 'recompose';

import { Checkbox, CheckboxGroup } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import StatusMessage from 'bundles/account-settings/components/common/StatusMessage';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { Sections } from 'bundles/account-settings/constants/SettingsConstants';
import type {
  CourseEmailPreference,
  CourseraNormalizedEmailPreference,
  EmailPreference,
  PartnerEmailPreference,
  UpdateStatus,
} from 'bundles/account-settings/types/EmailPreferences';
import Icon from 'bundles/iconfont/Icon';

import _t from 'i18n!nls/account-settings';

import 'css!../__styles__/EmailPreferencesList';

type PreferenceProps =
  | { type: typeof Sections.PARTNER_EMAIL_PREFERENCES; emailPreferences: PartnerEmailPreference[] }
  | { type: typeof Sections.COURSERA_EMAIL_PREFERENCES; emailPreferences: CourseraNormalizedEmailPreference[] }
  | { type: typeof Sections.COURSE_EMAIL_PREFERENCES; emailPreferences: CourseEmailPreference[] };

type PropsFromCaller = PreferenceProps & {
  disabled: boolean;
  updateStatus: UpdateStatus;
};

type PropsToComponent = PropsFromCaller & {
  client: ApolloClient<unknown>;
};

type State = {
  emailPreferences: EmailPreference[];
  clickedPreferences: { [key: string]: boolean }; // To track the clicked checkboxes on save
};

class EmailPreferencesList extends React.Component<PropsToComponent, State> {
  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);

    this.state = {
      emailPreferences: props.emailPreferences,
      clickedPreferences: {},
    };
  }

  static getDerivedStateFromProps(nextProps: PropsToComponent) {
    if (nextProps.updateStatus.state !== UpdateStates.UPDATING) {
      return {
        emailPreferences: nextProps.emailPreferences,
      };
    }

    return null;
  }

  componentDidMount(): void {
    this.trackPreferences('view_checkbox');
  }

  trackPreferences(action: 'view_checkbox' | 'click_checkbox') {
    this.state.emailPreferences.forEach((preference) => {
      if (action === 'view_checkbox' || this.state.clickedPreferences[preference.id]) {
        track(action, {
          checkbox: {
            name: 'coursera_communication_email_preference',
            label: preference.name,
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            checked: preference.value?.[preference.id],
          },
        });
      }
    });
  }

  handleUpdate = () => {
    const { client } = this.props;

    if (!_.isEmpty(this.state.emailPreferences)) {
      SettingsActionCreators.updateEmailPreferences(this.props.type, this.state.emailPreferences, client);

      // Tracking the clicked checkboxes on save.
      this.trackPreferences('click_checkbox');
      this.setState({ clickedPreferences: {} });
    }
  };

  renderSaveButton() {
    const { disabled } = this.props;

    let text: React.ReactNode = _t('Save');

    if (this.props.updateStatus.state === UpdateStates.UPDATING) {
      text = <Icon name="circle-o-notch" spin={true} />;
    }

    return (
      <button
        disabled={disabled}
        className="secondary cozy settings-primary-action"
        type="button"
        onClick={this.handleUpdate}
      >
        {text}
      </button>
    );
  }

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentVal = event.target.value;

    const updatedPreferences = this.state.emailPreferences;
    const updatedClickedPreferences = { ...this.state.clickedPreferences };

    const [preference] = _.filter(updatedPreferences, { id: currentVal });

    if (preference && preference.value) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      preference.value[currentVal] = !preference.value[currentVal];
      updatedClickedPreferences[currentVal] = true;

      SettingsActionCreators.updatePendingChangesStatus(this.props.type, true);

      this.setState({
        emailPreferences: updatedPreferences,
        clickedPreferences: updatedClickedPreferences,
      });
    }
  };

  render() {
    const selectedValues: React.ReactText[] = [];
    this.state.emailPreferences.forEach((pref) => {
      // @ts-expect-error Preferences type is not defined and hence it complains.
      if (pref.value[pref.id] === true) {
        selectedValues.push(pref.id);
      }
    });
    return (
      <div className="rc-EmailPreferencesList">
        <CheckboxGroup onChange={this.onChange} label={_t('Communication from Coursera')} value={selectedValues}>
          {this.state.emailPreferences.map((preference) => (
            <Checkbox
              disabled={this.props.disabled}
              label={preference.name}
              key={preference.id}
              value={preference.id}
            />
          ))}
        </CheckboxGroup>

        {this.renderSaveButton()}

        <StatusMessage status={this.props.updateStatus.state} />
      </div>
    );
  }
}

export default compose<PropsToComponent, PropsFromCaller>(withApollo)(EmailPreferencesList);
